import React from 'react'
import { useSelector } from 'react-redux'
import {
  Box, Paper, Typography, LinearProgress, Alert, Card, CardContent, Divider,
} from '@mui/material'
import { Loader, CheckCircle2, XCircle, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export default function BOExecutionProgress() {
  const { t } = useTranslation()
  const { selectedBo, executionProgress } = useSelector((s) => s.boConsolidation)
  const { step, percent, message, current, total, result } = executionProgress

  const isComplete = step === 'complete'
  const isError = step === 'error'

  return (
    <Paper sx={{ p: 4, borderRadius: 2 }}>
      {/* BO Info Header */}
      {selectedBo && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FileText size={24} color="#c9a96e" />
          <Box>
            <Typography
              variant="h6"
              sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 600 }}
            >
              {selectedBo.bo_num}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Error */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {message || t('du_bo_execution_error', 'An error occurred during execution.')}
        </Alert>
      )}

      {/* Progress */}
      {!isComplete && !isError && (
        <>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            <Loader size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} className="spin" />
            {t('du_bo_executing_docs', { n: total })}
          </Typography>

          {/* Progress bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {message || t('du_bo_processing', 'Processing...')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {Math.round(percent)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(201,169,110,0.15)',
                '& .MuiLinearProgress-bar': { backgroundColor: '#c9a96e', borderRadius: 3 },
              }}
            />
          </Box>

          {/* Counter */}
          {total > 0 && (
            <Typography variant="body2" color="text.secondary">
              {current}/{total} {t('du_bo_completed_count', 'completed')}
            </Typography>
          )}
        </>
      )}

      {/* Completion summary */}
      {isComplete && result && (
        <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <CheckCircle2 size={48} color="#4caf50" style={{ marginBottom: 8 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('du_bo_execution_done', 'Execution Complete')}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                  <CheckCircle2 size={16} color="#4caf50" />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
                    {result.success || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('du_bo_succeeded', 'Succeeded')}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                  <XCircle size={16} color={result.failed > 0 ? '#f44336' : '#bdbdbd'} />
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: result.failed > 0 ? '#f44336' : 'text.secondary' }}
                  >
                    {result.failed || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('du_bo_failed', 'Failed')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Spin animation for Loader icon */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1.5s linear infinite; }
      `}</style>
    </Paper>
  )
}
